
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DELIVERY_OPTIONS } from '../utils/formatDelivery';
import '../styles/AdminNotPageShowForm.css';
import ButtonRegular from './ui/ButtonRegular';
import ButtonDelete from './ui/ButtonDelete';

const AdminNotPageShowForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [form, setForm] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const deliveryMap = {
        inpost: 'Kurier InPost',
        inpost_cod: 'Kurier InPost za pobraniem',
    };

    const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

    useEffect(() => {
        const fetchForm = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, { headers: authHeader() });
                setForm(res.data);
            } catch (err) {
                setError('Nie udało się pobrać danych formularza.');
            } finally {
                setLoading(false);
            }
        };

        fetchForm();
    }, [id]);

    const startEditing = () => {
        setEditForm(JSON.parse(JSON.stringify(form)));
        setEditing(true);
        setError('');
        setSuccess('');
    };

    const cancelEditing = () => {
        setEditForm(null);
        setEditing(false);
        setError('');
    };

    const handleChange = (path, value) => {
        setEditForm(prev => {
            const updated = { ...prev };
            const keys = path.split('.');
            let source = prev;
            let target = updated;

            keys.forEach((key, index) => {
                if (index === keys.length - 1) {
                    target[key] = value;
                } else {
                    target[key] = { ...source[key] };
                    source = source[key];
                    target = target[key];
                }
            });

            return updated;
        });
    };

    const handleProductChange = (index, key, value) => {
        setEditForm(prev => ({
            ...prev,
            products: prev.products.map((product, i) => i === index ? { ...product, [key]: value } : product),
        }));
    };

    const saveChanges = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const payload = {
                products: editForm.products.map(product => ({ ...product, price: Number(product.price) || 0 })),
                customerData: editForm.customerData,
                shippingData: editForm.shippingData,
                deliveryMethod: editForm.deliveryMethod,
            };

            await axios.put(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, payload, { headers: authHeader() });

            setForm({ ...editForm, products: payload.products });
            setEditForm(null);
            setEditing(false);
            setSuccess('Zmiany zostały zapisane.');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Nie udało się zapisać zmian. Sprawdź endpoint w backendzie.');
        } finally {
            setSaving(false);
        }
    };

    const deleteForm = async () => {
        if (!window.confirm('Czy na pewno chcesz usunąć ten formularz?')) return;

        setSaving(true);

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, { headers: authHeader() });
            navigate('/admin/forms');
        } catch (err) {
            console.error(err);
            setError('Nie udało się usunąć formularza.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p>Ładowanie...</p>;
    if (!form) return <p className="error-text">{error || 'Formularz nie istnieje.'}</p>;

    const data = editing ? editForm : form;
    const { products = [], customerData = {}, shippingData = {}, deliveryMethod } = data;

    const currentDelivery = DELIVERY_OPTIONS.find(opt => opt.value === deliveryMethod);
    const totalPrice = products.reduce((sum, product) => sum + (Number(product.price) || 0), 0);
    const deliveryCost = Number(currentDelivery?.price) || 0;
    const grandTotal = totalPrice + deliveryCost;

    const showShippingData = Boolean(shippingData) && (
        (shippingData.street || '') !== (customerData.street || '') ||
        (shippingData.building || '') !== (customerData.building || '') ||
        (shippingData.apartment || '') !== (customerData.apartment || '') ||
        (shippingData.city || '') !== (customerData.city || '') ||
        (shippingData.postalCode || '') !== (customerData.postalCode || '')
    );

    const field = (label, path, value, type = 'text') => (
        <tr key={path}>
            <td>{label}</td>
            <td>
                {editing ? (
                    type === 'textarea' ? (
                        <textarea className="edit-form-input" value={value ?? ''} onChange={e => handleChange(path, e.target.value)} />
                    ) : type === 'boolean' || type === 'invoice' ? (
                        <select className="edit-form-input" value={value === true ? 'true' : 'false'} onChange={e => handleChange(path, e.target.value === 'true')}>
                            {type === 'boolean' ? (
                                <>
                                    <option value="true">Tak</option>
                                    <option value="false">Nie</option>
                                </>
                            ) : (
                                <>
                                    <option value="false">Paragon</option>
                                    <option value="true">Faktura VAT</option>
                                </>
                            )}
                        </select>
                    ) : (
                        <input className="edit-form-input" type={type} step={type === 'number' ? '0.01' : undefined} value={value ?? ''} onChange={e => handleChange(path, e.target.value)} />
                    )
                ) : (
                    type === 'boolean' ? (value ? 'Tak' : 'Nie') :
                    type === 'invoice' ? (value ? 'Faktura VAT' : 'Paragon') :
                    value ?? ''
                )}
            </td>
        </tr>
    );

    return (
        <div className="form-container">
            <div className="form-content">
                <h1 className="form-title">Podgląd formularza</h1>
                <h3 className="form-subtitle">Podgląd zamówienia nr: {form.formNumber ? String(form.formNumber).padStart(4, '0') : ''}</h3>

                {error && <p className="error-text">{error}</p>}
                {success && <p className="success-text">{success}</p>}

                <section className="form-section">
                    <h2>Produkty</h2>

                    {products.length === 0 ? (
                        <p>Brak produktów.</p>
                    ) : (
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Nazwa</th>
                                    <th style={{ textAlign: 'right' }}>Cena (PLN)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((product, index) => (
                                    <tr key={product._id || index} className={index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                                        <td>{index + 1}</td>
                                        <td>
                                            {editing ? (
                                                <input className="edit-form-input" value={product.name ?? ''} onChange={e => handleProductChange(index, 'name', e.target.value)} />
                                            ) : product.name}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {editing ? (
                                                <input className="edit-form-input price-input" type="number" min="0" step="0.01" value={product.price ?? ''} onChange={e => handleProductChange(index, 'price', e.target.value)} />
                                            ) : (Number(product.price) || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}

                                <tr className="table-total-row">
                                    <td colSpan={2}>Razem za produkty</td>
                                    <td style={{ textAlign: 'right' }}>{totalPrice.toFixed(2)}</td>
                                </tr>
                                <tr className="table-total-row">
                                    <td colSpan={2}>Koszt dostawy</td>
                                    <td style={{ textAlign: 'right' }}>{deliveryCost.toFixed(2)}</td>
                                </tr>
                                <tr className="table-total-row">
                                    <td colSpan={2}>Łącznie do zapłaty</td>
                                    <td style={{ textAlign: 'right' }}>{grandTotal.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </section>

                <section className="form-section">
                    <h2>Dane klienta</h2>
                    <table className="products-table">
                        <tbody>
                            {field('Imię', 'customerData.firstName', customerData.firstName)}
                            {field('Nazwisko', 'customerData.lastName', customerData.lastName)}
                            {field('Email', 'customerData.email', customerData.email, 'email')}
                            {field('Telefon', 'customerData.phone', customerData.phone, 'tel')}
                            {field('Ulica', 'customerData.street', customerData.street)}
                            {field('Numer budynku', 'customerData.building', customerData.building)}
                            {field('Numer lokalu', 'customerData.apartment', customerData.apartment)}
                            {field('Miasto', 'customerData.city', customerData.city)}
                            {field('Kod pocztowy', 'customerData.postalCode', customerData.postalCode)}
                            <tr>
                                <td>Dostawa</td>
                                <td>
                                    {editing ? (
                                        <select className="edit-form-input" value={deliveryMethod || ''} onChange={e => handleChange('deliveryMethod', e.target.value)}>
                                            {DELIVERY_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label || deliveryMap[option.value] || option.value}</option>)}
                                        </select>
                                    ) : deliveryMap[deliveryMethod] || deliveryMethod}
                                </td>
                            </tr>
                            {field('Adres dostawy taki sam jak dane zamawiającego', 'shippingData.sameAsCustomer', !showShippingData, 'boolean')}
                            {field('Typ dowodu księgowego', 'customerData.vatInvoice', customerData.vatInvoice, 'invoice')}
                            {field('Uwagi', 'customerData.additionalInfo', customerData.additionalInfo, 'textarea')}
                        </tbody>
                    </table>
                </section>

                {showShippingData && (
                    <section className="form-section red-bg">
                        <h2>Dane wysyłkowe</h2>
                        <table className="products-table">
                            <tbody>
                                {field('Ulica', 'shippingData.street', shippingData.street)}
                                {field('Numer budynku', 'shippingData.building', shippingData.building)}
                                {field('Numer lokalu', 'shippingData.apartment', shippingData.apartment)}
                                {field('Miasto', 'shippingData.city', shippingData.city)}
                                {field('Kod pocztowy', 'shippingData.postalCode', shippingData.postalCode)}
                            </tbody>
                        </table>
                    </section>
                )}

                {customerData.vatInvoice && (
                    <section className="form-section">
                        <h2>Dane do faktury VAT</h2>
                        <table className="products-table">
                            <tbody>
                                {field('Nazwa firmy', 'customerData.companyName', customerData.companyName)}
                                {field('Adres firmy', 'customerData.companyAddress', customerData.companyAddress)}
                                {field('NIP', 'customerData.taxId', customerData.taxId)}
                            </tbody>
                        </table>
                    </section>
                )}

                <div className="form-buttons">
                    <div className="action-buttons" style={{ marginTop: 8 }}>
                        {editing ? (
                            <>
                                <ButtonRegular onClick={saveChanges} disabled={saving}>{saving ? 'Zapisywanie...' : 'Zapisz'}</ButtonRegular>
                                <ButtonRegular onClick={cancelEditing} disabled={saving}>Anuluj</ButtonRegular>
                            </>
                        ) : (
                            <>
                                <ButtonRegular onClick={startEditing}>Edytuj</ButtonRegular>
                                <ButtonDelete onClick={deleteForm} disabled={saving}>{saving ? 'Usuwanie...' : 'Usuń cały formularz'}</ButtonDelete>
                            </>
                        )}
                        <ButtonRegular onClick={() => navigate('/admin/forms')} disabled={saving}>⬅️ Powrót</ButtonRegular>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminNotPageShowForm;
